/**
 * 一筆推薦點的編輯卡片。
 *
 * 一張卡片 = 船上 14 個檔案（清單頁的店名與語音提示、詳情頁的五個欄位與兩張圖、
 * 跨三語系）。刻意不讓編輯的人看到檔名結構：填「營業時間」比填
 * `5_2_str4.txt` 容易得多，而且檔名怎麼排本來就不需要人來判斷。
 *
 * **即時儲存**：文字欄位在失焦時送出（每個按鍵一次 PUT 太吵），評分與圖片改完
 * 立刻送。一個景點有 20 張卡片，等表單統一送出會讓「我剛改的存了沒」無從判斷。
 */

import { useEffect, useState } from 'react';

import { Button } from './Button';
import { MediaPicker } from './MediaPicker';
import { MediaThumbById } from './MediaPreview';
import { toDisplayLocale } from './SpotMediaFields';
import { FIELD_MAX_WIDTH } from './spot-form';
import { clearPoiMarkPatch } from './spot-poi-translation';
import { suggestAsrHint, type SpotPoi, type SpotPoiPatch } from '../lib/api-client';

/** 卡片上的文字欄位；`output` 只在展開的說明裡出現，平時不干擾。 */
const TEXT_FIELDS = [
  { key: 'name_i18n', label: '店名', output: '4_str{n}.txt · 5_{n}_str1.txt', rows: 1 },
  { key: 'asr_hint_i18n', label: '語音提示', output: '4_str{n+5}.txt', rows: 1 },
  { key: 'nearby_i18n', label: '所在地', output: '5_{n}_str2.txt', rows: 1 },
  { key: 'intro_i18n', label: '簡介', output: '5_{n}_str3.txt', rows: 2 },
  { key: 'hours_i18n', label: '營業時間', output: '5_{n}_str4.txt', rows: 1 },
] as const;

type TextKey = (typeof TEXT_FIELDS)[number]['key'];

/**
 * 兩個可挑選的圖片插槽。
 *
 * `group` 是船上的畫面層級，也就是資料夾名的前綴（`4_food/`、`5_food/`）——就地
 * 上傳時要用它算出標籤，讓檔案直接落在「從資料夾套用」會去掃的那個位置。
 */
const IMAGE_SLOTS = [
  { key: 'thumb_media_id', label: '清單縮圖', output: '4_pic{n}.png', group: 4 },
  { key: 'photo_media_id', label: '詳情大圖', output: '5_{n}_pic1.png', group: 5 },
] as const;

type ImageKey = (typeof IMAGE_SLOTS)[number]['key'];

interface Props {
  poi: SpotPoi;
  /** CMS 語系代碼（zh-TW / en / ja）——i18n 物件的鍵。 */
  locale: string;
  localeLabel: string;
  /** 景點的窗屏編號；挑選素材時限定在這個資料夾底下。未設定則不限定。 */
  roiId?: string;
  index: number;
  total: number;
  saving: boolean;
  onPatch: (patch: SpotPoiPatch) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}

export function SpotPoiCard({
  poi,
  locale,
  localeLabel,
  roiId,
  index,
  total,
  saving,
  onPatch,
  onMove,
  onRemove,
}: Props): JSX.Element {
  const [draft, setDraft] = useState<Record<TextKey, string>>(() => textDraft(poi, locale));
  const [ratingDraft, setRatingDraft] = useState<string>(
    poi.rating === null ? '' : String(poi.rating),
  );
  const [mapDraft, setMapDraft] = useState<string>(poi.map_url ?? '');
  const [picking, setPicking] = useState<ImageKey | null>(null);

  // 伺服器版本變了（切語系、資料夾套用、他人編輯）就重置草稿——沒有這段的話
  // 切到英文頁籤仍會看到中文的值
  useEffect(() => {
    setDraft(textDraft(poi, locale));
    setRatingDraft(poi.rating === null ? '' : String(poi.rating));
    setMapDraft(poi.map_url ?? '');
  }, [poi, locale]);

  /**
   * 只在真的變了才送——失焦事件在單純點過去又點開時也會觸發。
   *
   * 人動手改過的格子要順手解除「機翻待校」記號：那個記號的意思是「機器翻的、還
   * 沒人看過」，改過就不再成立。少了這一步，校對完的內容會一直掛著黃色警示，久了
   * 就沒有人再理會它。
   */
  const commitText = (key: TextKey): void => {
    const next = draft[key].trim();
    const current = (poi[key] as Record<string, string> | undefined)?.[locale] ?? '';
    if (next === current) return;
    const cleared = clearPoiMarkPatch(poi, key, locale);
    onPatch({
      [key]: { ...(poi[key] as Record<string, string>), [locale]: next },
      ...(cleared ? { translation_state: cleared } : {}),
    });
  };

  const commitRating = (): void => {
    const raw = ratingDraft.trim();
    const next = raw === '' ? null : Number(raw);
    if (next !== null && (Number.isNaN(next) || next < 0 || next > 5)) return;
    if (next === poi.rating) return;
    // 一位小數：資料庫是 numeric(2,1)，多送的位數會被四捨五入，顯示值就對不上
    onPatch({ rating: next === null ? null : Math.round(next * 10) / 10 });
  };

  const pickingSlot = IMAGE_SLOTS.find((s) => s.key === picking) ?? null;
  /** 窗屏檔案樹的語系代碼（zh / en / jp），與 CMS 的 zh-TW / en / ja 不同。 */
  const displayLocale = toDisplayLocale(locale);
  /** 這個景點在媒體庫裡的資料夾；沒有窗屏編號時不打資料夾標籤。 */
  const folderTag = roiId && roiId.trim() !== '' ? `${roiId.trim()}/${displayLocale}` : null;

  const name = draft.name_i18n;
  const hintSuggestion = suggestAsrHint(name, locale === 'en' ? 'en' : 'zh');
  const showHintSuggestion = name !== '' && draft.asr_hint_i18n === '' && hintSuggestion !== '';

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 12,
        marginBottom: 10,
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>#{poi.seq}</strong>
        <span style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>
          {name || <em style={{ color: '#d1d5db' }}>（未命名，{localeLabel}）</em>}
        </span>
        <IconButton title="上移" disabled={index === 0 || saving} onClick={() => onMove(-1)}>
          ↑
        </IconButton>
        <IconButton title="下移" disabled={index === total - 1 || saving} onClick={() => onMove(1)}>
          ↓
        </IconButton>
        <IconButton title="刪除這筆推薦點" disabled={saving} onClick={onRemove}>
          🗑
        </IconButton>
      </div>

      {TEXT_FIELDS.map((f) => (
        <div key={f.key} style={{ marginBottom: 8, maxWidth: FIELD_MAX_WIDTH }}>
          <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 2 }}>
            {f.label}
            <span
              style={{ marginLeft: 6, fontSize: 10, color: '#d1d5db', fontFamily: 'monospace' }}
            >
              {f.output}
            </span>
          </label>
          {f.rows > 1 ? (
            <textarea
              value={draft[f.key]}
              rows={f.rows}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              onBlur={() => commitText(f.key)}
              style={inputStyle}
            />
          ) : (
            <input
              type="text"
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              onBlur={() => commitText(f.key)}
              style={inputStyle}
            />
          )}
          {f.key === 'asr_hint_i18n' && showHintSuggestion && (
            <button
              type="button"
              onClick={() => {
                setDraft({ ...draft, asr_hint_i18n: hintSuggestion });
                onPatch({
                  asr_hint_i18n: { ...(poi.asr_hint_i18n ?? {}), [locale]: hintSuggestion },
                });
              }}
              style={{
                marginTop: 2,
                fontSize: 11,
                color: '#2563eb',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              建議：{hintSuggestion}（點擊填入）
            </button>
          )}
        </div>
      ))}

      {/* 上限同上：地圖連結是 flex:1，不收就會拉滿整個寬螢幕，與上方欄位切齊不了 */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 8,
          flexWrap: 'wrap',
          maxWidth: FIELD_MAX_WIDTH,
        }}
      >
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 2 }}>
            評分
            <span
              style={{ marginLeft: 6, fontSize: 10, color: '#d1d5db', fontFamily: 'monospace' }}
            >
              5_{'{n}'}_str5.txt
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="number"
              min={0}
              max={5}
              step={0.1}
              value={ratingDraft}
              onChange={(e) => setRatingDraft(e.target.value)}
              onBlur={commitRating}
              style={{ ...inputStyle, width: 80 }}
            />
            {/* 單位詞由產出端依語系補（zh「顆星」、en「Stars」），這裡只填數字 */}
            <span style={{ fontSize: 12, color: '#9ca3af' }}>顆星</span>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 2 }}>
            地圖連結
            <span style={{ marginLeft: 6, fontSize: 10, color: '#d1d5db' }}>→ QR 圖自動生成</span>
          </label>
          <input
            type="url"
            value={mapDraft}
            placeholder="https://maps.app.goo.gl/…"
            onChange={(e) => setMapDraft(e.target.value)}
            onBlur={() => {
              const next = mapDraft.trim() === '' ? null : mapDraft.trim();
              if (next !== (poi.map_url ?? null)) onPatch({ map_url: next });
            }}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {IMAGE_SLOTS.map((s) => (
          <div key={s.key} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <MediaThumbById id={poi[s.key]} alt={s.label} />
            <div>
              <div style={{ fontSize: 12 }}>{s.label}</div>
              <div style={{ fontSize: 10, color: '#d1d5db', fontFamily: 'monospace' }}>
                {s.output}
              </div>
              <Button variant="secondary" onClick={() => setPicking(s.key)} disabled={saving}>
                {poi[s.key] ? '更換' : '選擇'}
              </Button>
              {poi[s.key] && (
                <Button
                  variant="ghost"
                  onClick={() => onPatch({ [s.key]: null })}
                  disabled={saving}
                >
                  清除
                </Button>
              )}
            </div>
          </div>
        ))}

        {/* QR 由 map_url 生成，不提供手動挑選——人工做圖只會與連結不同步 */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <MediaThumbById id={poi.qr_media_id} alt="地圖 QR" />
          <div style={{ fontSize: 12 }}>
            地圖 QR
            <div style={{ fontSize: 10, color: '#d1d5db', fontFamily: 'monospace' }}>
              5_{'{n}'}_pic2.png
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              {poi.qr_media_id ? '已生成' : '填入地圖連結後於發布時生成'}
            </div>
          </div>
        </div>
      </div>

      {pickingSlot && (
        <MediaPicker
          open
          title={`選擇「${pickingSlot.label}」（PNG）`}
          mediaType="image"
          acceptMimes={['image/png']}
          multiple={false}
          initialSelected={poi[pickingSlot.key] ? [poi[pickingSlot.key] as string] : []}
          /*
           * 就地上傳，與二階畫面的插槽同一套規則：拖進來就上傳並選取，語系與資料夾
           * 標籤由插槽算好。差別只在推薦點的資料夾多兩層——`{roi}/{lang}/{4,5}_{分類}`
           * 才是「從資料夾套用」會去掃的位置，少算一層的話檔案上得去卻掃不到。
           */
          upload={{
            language: displayLocale,
            tags: folderTag === null ? [] : [`${folderTag}/${pickingSlot.group}_${poi.category}`],
          }}
          /* 挑選一律限定在這個景點的資料夾底下（沒有窗屏編號時無從限定） */
          {...(roiId && roiId.trim() !== '' ? { restrictToFolder: roiId.trim() } : {})}
          onConfirm={(ids) => {
            onPatch({ [pickingSlot.key]: ids[0] ?? null });
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  border: '1px solid #d1d5db',
  borderRadius: 4,
};

function textDraft(poi: SpotPoi, locale: string): Record<TextKey, string> {
  const out = {} as Record<TextKey, string>;
  for (const f of TEXT_FIELDS) {
    out[f.key] = (poi[f.key] as Record<string, string> | undefined)?.[locale] ?? '';
  }
  return out;
}

function IconButton({
  title,
  disabled,
  onClick,
  children,
}: {
  title: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        border: '1px solid #e5e7eb',
        background: '#fff',
        borderRadius: 4,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        padding: '2px 6px',
        fontSize: 12,
      }}
    >
      {children}
    </button>
  );
}
